import { Button, DataTable, StatusBadge } from '@skylabs-digital/backoffice-kit';
import type { ColumnDef } from '@skylabs-digital/tables-react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ActivityEvent } from '../../schemas/activity.schema';

const TONE: Record<ActivityEvent['type'], 'success' | 'info' | 'danger' | 'warning' | 'neutral'> = {
  'file.uploaded': 'info',
  'file.processed': 'success',
  'file.processing_failed': 'danger',
  'file.deleted': 'neutral',
  'app.quota_threshold': 'warning',
};

function detail(e: ActivityEvent): string {
  if (e.type === 'file.deleted' && typeof e.data.reason === 'string')
    return `reason: ${e.data.reason}`;
  if (e.type === 'app.quota_threshold' && typeof e.data.percent === 'number') {
    return `${e.data.percent}% of quota`;
  }
  return '';
}

export function ActivityTable({
  rows,
  loading,
  error,
}: {
  rows: ActivityEvent[];
  loading: boolean;
  error: Error | null;
}) {
  const navigate = useNavigate();
  const columns = useMemo<ColumnDef<ActivityEvent>[]>(
    () => [
      {
        id: 'when',
        header: 'When',
        cell: ({ row }) => (
          <span className="text-sm">{new Date(row.original.occurredAt).toLocaleString()}</span>
        ),
      },
      {
        accessorKey: 'type',
        header: 'Event',
        cell: ({ row }) => (
          <StatusBadge tone={TONE[row.original.type]} dot={false}>
            {row.original.type}
          </StatusBadge>
        ),
      },
      {
        accessorKey: 'appName',
        header: 'App',
        cell: ({ row }) => (
          <Button
            variant="link"
            className="h-auto p-0"
            onClick={() => navigate(`/apps/${row.original.appId}`)}
          >
            {row.original.appName}
          </Button>
        ),
      },
      {
        id: 'file',
        header: 'File',
        cell: ({ row }) =>
          row.original.filePath ? (
            <code className="font-mono text-xs">{row.original.filePath}</code>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        id: 'detail',
        header: 'Detail',
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">{detail(row.original)}</span>
        ),
      },
      {
        accessorKey: 'id',
        header: 'Event id',
        cell: ({ row }) => (
          <code className="font-mono text-xs text-muted-foreground">{row.original.id}</code>
        ),
      },
    ],
    [navigate],
  );
  return (
    <DataTable
      columns={columns}
      data={rows}
      isLoading={loading}
      error={error}
      hidePagination
      aria-label="Activity"
      empty={<p className="py-6 text-center text-sm text-muted-foreground">No events match</p>}
    />
  );
}
